import type { Rng } from '../rng';
import { clamp } from '../math';
import { effectiveRange, effectiveValues, isLocked, type LayerBulk, type ParamBulkSpec } from './bulk';
import { normaliseCurve, type Curve } from './curve';
import type { FloatParam, IntParam, Param, ParamSchema, ParamValue, ParamValues } from './types';

/**
 * Three strengths of randomisation, all respecting locks and exploration ranges.
 *
 *  - `gentle` nudges numeric values by ±10 % of their range and rarely flips a switch.
 *  - `medium` moves by ±40 % and changes discrete choices about half the time.
 *  - `wild` redraws everything from the full allowed range.
 *
 * The first two perturb the *current* value rather than redrawing it, which is what
 * makes them useful: they explore the neighbourhood of something you already like.
 */
export type RandomizeStrength = 'gentle' | 'medium' | 'wild';

interface StrengthProfile {
  /** Fraction of the range a numeric value may move. */
  spread: number;
  /** Probability that a discrete parameter is redrawn at all. */
  discreteChance: number;
}

const PROFILES: Record<RandomizeStrength, StrengthProfile> = {
  gentle: { spread: 0.1, discreteChance: 0.1 },
  medium: { spread: 0.4, discreteChance: 0.45 },
  wild: { spread: Infinity, discreteChance: 1 },
};

export const STRENGTH_LABELS: Record<RandomizeStrength, string> = {
  gentle: 'Sanft',
  medium: 'Mittel',
  wild: 'Wild',
};

function drawNumber(lo: number, hi: number, spec: ParamBulkSpec | undefined, rng: Rng): number {
  if (hi <= lo) return lo;
  switch (spec?.distribution) {
    case 'normal': {
      // Centre on the middle of the range; resample rather than clamp so the
      // distribution keeps its shape instead of piling up at the bounds.
      const mid = (lo + hi) / 2;
      const sigma = (hi - lo) * (spec.sigma ?? 0.25);
      for (let i = 0; i < 12; i++) {
        const v = rng.gaussian(mid, sigma);
        if (v >= lo && v <= hi) return v;
      }
      return clamp(rng.gaussian(mid, sigma), lo, hi);
    }
    case 'exponential': {
      // Biased towards the lower bound; the /3 keeps most draws inside the range.
      const v = lo + rng.exponential(3 / (hi - lo));
      return v > hi ? lo + rng.next() * (hi - lo) : v;
    }
    default:
      return lo + rng.next() * (hi - lo);
  }
}

function quantise(param: FloatParam | IntParam, v: number): number {
  const snapped = Math.round(v / param.step) * param.step;
  return param.kind === 'int' ? Math.round(snapped) : Number(snapped.toFixed(6));
}

/** Randomises a single parameter. Returns the current value unchanged when locked. */
export function randomizeParam(
  param: Param,
  current: ParamValue,
  spec: ParamBulkSpec | undefined,
  rng: Rng,
  strength: RandomizeStrength = 'wild',
): ParamValue {
  if (param.neverRandomise || spec?.locked) return current;
  const profile = PROFILES[strength];

  switch (param.kind) {
    case 'float':
    case 'int': {
      const [lo, hi] = effectiveRange(param, spec);
      if (profile.spread === Infinity) {
        return quantise(param, clamp(drawNumber(lo, hi, spec, rng), lo, hi));
      }
      const base = typeof current === 'number' ? current : param.default;
      const delta = (hi - lo) * profile.spread;
      return quantise(param, clamp(base + rng.signed(delta), lo, hi));
    }

    case 'bool': {
      if (!rng.chance(profile.discreteChance)) return current;
      return rng.chance(param.bulkProbability ?? 0.5);
    }

    case 'enum': {
      if (!rng.chance(profile.discreteChance)) return current;
      const values = effectiveValues(param, spec);
      return values.length ? rng.pick(values) : current;
    }

    case 'text': {
      if (!rng.chance(profile.discreteChance)) return current;
      const values = effectiveValues(param, spec);
      return values.length ? rng.pick(values) : current;
    }

    case 'range': {
      const [lo, hi] = [param.min, param.max];
      const a = drawNumber(lo, hi, spec, rng);
      const b = drawNumber(lo, hi, spec, rng);
      const sorted: [number, number] = a <= b ? [a, b] : [b, a];
      if (profile.spread === Infinity) return sorted;
      const cur = Array.isArray(current) ? (current as [number, number]) : param.default;
      const delta = (hi - lo) * profile.spread;
      const c0 = clamp(cur[0] + rng.signed(delta), lo, hi);
      const c1 = clamp(cur[1] + rng.signed(delta), lo, hi);
      return c0 <= c1 ? [c0, c1] : [c1, c0];
    }

    case 'vec2': {
      const cur = (current as { x: number; y: number }) ?? param.default;
      if (profile.spread === Infinity) {
        return {
          x: param.min.x + rng.next() * (param.max.x - param.min.x),
          y: param.min.y + rng.next() * (param.max.y - param.min.y),
        };
      }
      return {
        x: clamp(cur.x + rng.signed((param.max.x - param.min.x) * profile.spread), param.min.x, param.max.x),
        y: clamp(cur.y + rng.signed((param.max.y - param.min.y) * profile.spread), param.min.y, param.max.y),
      };
    }

    case 'curve': {
      if (!rng.chance(profile.discreteChance)) return current;
      const cur = (Array.isArray(current) ? current : param.default) as Curve;
      const jitter = profile.spread === Infinity ? 0.5 : profile.spread;
      return normaliseCurve(
        cur.map((p, i) => ({
          // Interior points may move in x; the endpoints stay pinned.
          x: i === 0 || i === cur.length - 1 ? p.x : clamp(p.x + rng.signed(jitter * 0.3), 0.02, 0.98),
          y: clamp(p.y + rng.signed(jitter), 0, 1),
        })),
      );
    }

    case 'color': {
      if (!rng.chance(profile.discreteChance)) return current;
      const h = Math.floor(rng.next() * 360);
      const s = 45 + rng.next() * 45;
      const l = 40 + rng.next() * 35;
      return hslToHex(h, s, l);
    }

    case 'seed':
      return Math.floor(rng.next() * 999999999);

    default:
      return current;
  }
}

/** Randomises every unlocked parameter of a schema. */
export function randomizeValues(
  schema: ParamSchema,
  values: ParamValues,
  bulk: LayerBulk | undefined,
  rng: Rng,
  strength: RandomizeStrength = 'medium',
): ParamValues {
  const out: ParamValues = { ...values };
  for (const param of schema) {
    if (isLocked(bulk, param.key)) continue;
    out[param.key] = randomizeParam(param, values[param.key], bulk?.[param.key], rng, strength);
  }
  return out;
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100;
  const ln = l / 100;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number): number => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (v: number): string =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}
